import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Boxes, Flame, Sparkles } from "lucide-react";
import type { PackDetailDto } from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { rm, credits, timeAgo } from "../lib/format";
import { EASE } from "../lib/motion";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { ProductImage } from "../components/ProductCard";
import { ListingTitle } from "../components/ListingTitle";
import { TierBadge } from "../components/storefront/TierBadge";
import { SectionHeader } from "../components/storefront/SectionHeader";
import { Button, PageLoader } from "../components/ui";
import { PackOpeningCeremony } from "../components/packs";
import type { PullOutcome } from "../components/packs";
import { PackVisual } from "../components/fx/PackVisual";
import { HoloCard, Particles, Reveal } from "../components/fx";
import { usePrefersReducedMotion } from "../hooks/useBreakpoint";

export function PackDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, config } = useAuth();
  const reduced = usePrefersReducedMotion();
  const [ceremony, setCeremony] = useState<{ count: number; run: number } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["pack", id],
    queryFn: () => api<{ pack: PackDetailDto }>(`/packs/${id}`),
  });

  const { data: feedData } = useQuery({
    queryKey: ["pack-feed"],
    queryFn: () =>
      api<{
        items: {
          packName: string;
          tierName: string;
          tierColor: string;
          productName: string;
          productImage: string | null;
          handle: string;
          at: string;
        }[];
      }>("/packs/feed"),
  });

  const pack = data?.pack;

  const commit = useCallback(
    async (count: number) => {
      if (!pack) throw new Error("Pack unavailable");
      try {
        const res = await api<{ pulls: PullOutcome[]; creditsSpent: number }>(`/packs/${pack.id}/open`, {
          body: { count },
        });
        await queryClient.invalidateQueries({ queryKey: ["pack", id] });
        await queryClient.invalidateQueries({ queryKey: ["wallet"] });
        await queryClient.invalidateQueries({ queryKey: ["pack-feed"] });
        return res.pulls;
      } catch (err) {
        toast("error", err instanceof ApiError ? err.message : "Couldn't open the pack");
        throw err;
      }
    },
    [pack, queryClient, id],
  );

  if (isLoading) return <PageLoader />;
  if (!pack) return null;

  const recentPulls = feedData?.items.filter((p) => p.packName === pack.name).slice(0, 3) ?? [];
  const soldOut = pack.totalRemaining === 0;
  const openButtons = pack.openOffersDisplay?.length
    ? pack.openOffersDisplay
    : [{ quantity: 1, cashTotal: pack.price, creditTotal: pack.creditPrice ?? pack.price, savingsPctCredit: 0, savingsPctCash: 0 }];
  const poolValue = pack.tiers.reduce(
    (sum, tier) => sum + tier.items.reduce((s, item) => s + item.estValue * item.remainingQty, 0),
    0,
  );

  function startOpening(count: number) {
    if (!user) {
      navigate("/login");
      return;
    }
    setCeremony({ count, run: Date.now() });
  }

  return (
    <>
      {ceremony && (
        <PackOpeningCeremony
          key={ceremony.run}
          open
          packId={pack.id}
          packName={pack.name}
          packImage={pack.images[0] ?? null}
          foilLabel={pack.foilLabel}
          foilColor={pack.foilColor}
          count={ceremony.count}
          onCommit={commit}
          onClose={() => setCeremony(null)}
          onOpenAnother={() => setCeremony({ count: ceremony.count, run: Date.now() })}
        />
      )}

      <div className="min-w-0 max-w-full overflow-x-hidden px-4 pt-4 lg:px-6">
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:items-stretch lg:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE.outExpo }}
            className="relative flex h-full min-h-0 overflow-hidden rounded-3xl neon-ring glow-brand bg-mesh-deep bg-noise"
          >
            <Particles count={16} />
            <div className="relative flex flex-1 flex-col items-center justify-center px-6 py-10 lg:px-5 lg:py-6">
              <div className={reduced ? "" : "float-soft"}>
                <HoloCard holo={0.35} max={14} lift={22}>
                  <PackVisual
                    id={pack.id}
                    name={pack.name}
                    images={pack.images}
                    foilLabel={pack.foilLabel}
                    foilColor={pack.foilColor}
                    className="h-[260px] w-[182px] shadow-2xl lg:h-[190px] lg:w-[133px]"
                  />
                </HoloCard>
              </div>

              <h1 className="mt-6 text-center font-display text-xl font-extrabold lg:mt-4 lg:text-xl">
                {pack.name}
              </h1>
              <div className="mt-2 flex flex-wrap items-center justify-center gap-2 text-[10px] lg:mt-2.5">
                <Chip icon={Boxes} label={`${pack.totalRemaining} cards in pool`} />
                <Chip icon={Flame} label={pack.mode === "DECLINING" ? "Declining odds" : "Fixed odds"} />
              </div>
            </div>
          </motion.div>

          <div className="mt-5 flex flex-col lg:sticky lg:top-20 lg:mt-0">
            <div className="glass-panel space-y-3 rounded-3xl p-4">
              <div className="flex items-baseline justify-between">
                <span className="text-[11px] uppercase tracking-widest text-white/40">Pool value</span>
                <span className="font-display text-lg font-bold text-gradient">{rm(poolValue)}</span>
              </div>

              {config?.creditsEnabled && (
                <div className="space-y-2">
                  {openButtons.map((offer, index) => {
                    const disabled = soldOut || pack.totalRemaining < offer.quantity;
                    const isPrimary = index === 0;
                    const savings = offer.savingsPctCredit;
                    return (
                      <Button
                        key={offer.quantity}
                        className={isPrimary ? "w-full py-3.5 text-base" : "w-full"}
                        variant={isPrimary ? "primary" : "outline"}
                        disabled={disabled}
                        onClick={() => startOpening(offer.quantity)}
                      >
                        <span className="inline-flex flex-wrap items-center justify-center gap-2">
                          {isPrimary && <Sparkles size={16} />}
                          Open ×{offer.quantity} · {credits(offer.creditTotal)}
                          {savings > 0 && (
                            <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold text-emerald-300">
                              Save {savings}%
                            </span>
                          )}
                        </span>
                      </Button>
                    );
                  })}
                </div>
              )}

              {soldOut && (
                <p className="rounded-xl bg-amber-500/10 px-3 py-2 text-center text-[11px] text-amber-300">
                  This pool is emptied out. New stock drops soon.
                </p>
              )}

              <p className="text-center text-[10px] leading-relaxed text-white/35">
                ×1 — spin and pick your pack · ×N bulk — rip straight into the haul
              </p>
            </div>

            {recentPulls.length > 0 && (
              <div className="mt-4 hidden flex-col rounded-3xl border border-white/5 bg-surface/60 p-4 lg:flex">
                <p className="mb-3 flex shrink-0 items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/40">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-ring" /> Recent pulls
                </p>
                <ul className="flex h-[124px] flex-col gap-2 overflow-hidden">
                  {recentPulls.map((pull, i) => (
                    <li key={`${pull.at}-${i}`} className="flex h-9 shrink-0 items-center gap-2.5">
                      <ProductImage src={pull.productImage} alt="" className="h-9 w-9 shrink-0 rounded-lg" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[11px] font-medium">{pull.productName}</p>
                        <p className="text-[9px] text-white/40">
                          {pull.handle} · {timeAgo(pull.at)}
                        </p>
                      </div>
                      <TierBadge name={pull.tierName} color={pull.tierColor} size="sm" />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {recentPulls.length > 0 && (
          <section className="mt-8 min-w-0 lg:hidden">
            <SectionHeader title="Live pulls" hint="from this pack" />
            <div className="scroll-row pb-1">
              {recentPulls.map((pull, i) => (
                <div
                  key={`${pull.at}-${i}`}
                  className="flex w-56 shrink-0 items-center gap-2 rounded-xl border border-white/5 bg-white/[0.03] p-2"
                >
                  <ProductImage src={pull.productImage} alt="" className="h-10 w-10 rounded-lg" />
                  <div className="min-w-0">
                    <p className="truncate text-[10px] font-semibold">{pull.productName}</p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[9px] text-white/45">
                      <TierBadge name={pull.tierName} color={pull.tierColor} size="sm" />
                      <span>
                        {pull.handle} · {timeAgo(pull.at)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-8 min-w-0 space-y-3">
          <SectionHeader title="What's inside" hint="every card, every odd" />

          <div className="flex flex-col gap-3">
            {pack.tiers.map((tier, ti) => (
              <Reveal key={tier.id} delay={ti * 0.05}>
                <div className="-mx-4 overflow-hidden border-y border-line bg-surface/80 lg:-mx-6">
                  <div className="px-4 py-3.5 lg:px-6">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <TierBadge name={tier.name} color={tier.color} glow />
                      <span
                        className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                        style={{ background: `${tier.color}22`, color: tier.color }}
                      >
                        {tier.oddsPct}% odds
                      </span>
                    </div>

                    <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: tier.color }}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${Math.min(100, tier.oddsPct)}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: EASE.outExpo }}
                      />
                    </div>
                  </div>

                  <div className="scroll-row gap-3 px-4 pb-4 lg:px-6">
                    {tier.items.map((item) => (
                      <div
                        key={item.id}
                        className={`w-[9.5rem] shrink-0 snap-start rounded-xl border border-white/5 bg-white/[0.03] p-2 transition-transform sm:w-36 lg:hover:-translate-y-1 ${
                          item.remainingQty === 0 ? "opacity-40" : ""
                        }`}
                      >
                        <ProductImage src={item.image} alt="" className="aspect-square w-full rounded-lg" />
                        <ListingTitle className="mt-1.5 text-[10px] leading-[14px] min-h-[14px]">{item.name}</ListingTitle>
                        <p className="text-[9px] text-white/40">est. {rm(item.estValue)}</p>
                        <p
                          className={`mt-0.5 text-[9px] font-semibold ${
                            item.remainingQty === 0 ? "text-white/30 line-through" : "text-white/70"
                          }`}
                        >
                          {item.remainingQty}/{item.initialQty} left
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

function Chip({ icon: Icon, label }: { icon: typeof Boxes; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-white/70">
      <Icon size={12} className="text-brand-pink" />
      {label}
    </span>
  );
}
